/**
 * Zod schema for the Contact Us form. Messages are i18n keys in the `shared` namespace, resolved
 * through `t()` by the field components, matching `profile.schema.ts`. String fields trim in the
 * shape rather than at each comparison, so every check and the parsed output agree on the value
 * that actually gets submitted — same shape as `referAFriend.schema.ts`.
 *
 * Bounds mirror the backend's `SubmitContactUsRequestInputSchema`: every field required, a real
 * email format on `email`, `message` capped at 4000 characters, and `agreedToPhiDisclaimer` true.
 * Legacy validated none of this client-side, so an invalid email reached the server and failed the
 * whole page after the member had already typed their message.
 */
import { z } from 'zod';

export const CONTACT_US_VALIDATION_KEYS = {
  categoryRequired: 'contactUs.validation.categoryRequired',
  nameRequired: 'contactUs.validation.nameRequired',
  emailRequired: 'contactUs.validation.emailRequired',
  emailInvalid: 'contactUs.validation.emailInvalid',
  callbackNumberInvalid: 'contactUs.validation.callbackNumberInvalid',
  messageRequired: 'contactUs.validation.messageRequired',
  messageTooLong: 'contactUs.validation.messageTooLong',
  phiDisclaimerRequired: 'contactUs.validation.phiDisclaimerRequired',
  /* Not a validation rule: the category field borrows this slot to explain a failed
     `contactUsCategories` fetch, since an empty dropdown is otherwise unexplained. */
  categoriesUnavailable: 'contactUs.validation.categoriesUnavailable',
} as const;

/** Matches the backend's own cap; also applied as `maxLength` on the Message control. */
export const CONTACT_US_MESSAGE_MAX_LENGTH = 4000;

const CALLBACK_NUMBER_DIGITS = 10;

const isValidEmail = (email: string) => z.email().safeParse(email).success;

export const contactUsSchema = z
  .object({
    categoryKey: z.string(),
    name: z.string().trim(),
    email: z.string().trim(),
    callbackNumber: z.string().trim(),
    message: z.string().trim(),
    agreedToPhiDisclaimer: z.boolean(),
  })
  .superRefine((values, ctx) => {
    if (values.categoryKey === '') {
      ctx.addIssue({ code: 'custom', path: ['categoryKey'], message: CONTACT_US_VALIDATION_KEYS.categoryRequired });
    }

    if (values.name === '') {
      ctx.addIssue({ code: 'custom', path: ['name'], message: CONTACT_US_VALIDATION_KEYS.nameRequired });
    }

    if (values.email === '') {
      ctx.addIssue({ code: 'custom', path: ['email'], message: CONTACT_US_VALIDATION_KEYS.emailRequired });
    } else if (!isValidEmail(values.email)) {
      ctx.addIssue({ code: 'custom', path: ['email'], message: CONTACT_US_VALIDATION_KEYS.emailInvalid });
    }

    // Empty and malformed share one message ("A valid phone number is required."), per the design.
    if (values.callbackNumber.replace(/\D/g, '').length !== CALLBACK_NUMBER_DIGITS) {
      ctx.addIssue({
        code: 'custom',
        path: ['callbackNumber'],
        message: CONTACT_US_VALIDATION_KEYS.callbackNumberInvalid,
      });
    }

    if (values.message === '') {
      ctx.addIssue({ code: 'custom', path: ['message'], message: CONTACT_US_VALIDATION_KEYS.messageRequired });
    } else if (values.message.length > CONTACT_US_MESSAGE_MAX_LENGTH) {
      ctx.addIssue({ code: 'custom', path: ['message'], message: CONTACT_US_VALIDATION_KEYS.messageTooLong });
    }

    if (!values.agreedToPhiDisclaimer) {
      ctx.addIssue({
        code: 'custom',
        path: ['agreedToPhiDisclaimer'],
        message: CONTACT_US_VALIDATION_KEYS.phiDisclaimerRequired,
      });
    }
  });

export type ContactUsSchemaValues = z.infer<typeof contactUsSchema>;
